<script lang="ts">
export default {
  name: 'AddPromptMappingModal',
};
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import type { AIPrompt, AIPromptMappingRequest } from '@/types/ai';
import { PROMPT_VERSION_LATEST } from '@/types/ai';
import { aiService } from '@/services/AIService';
import { useToast } from '@/composables/useToast';

interface CoverageType {
  id: number;
  name: string;
}

interface DocumentType {
  id: number;
  name: string;
}

const props = defineProps<{
  prompts: AIPrompt[];
  coverageTypes: CoverageType[];
  documentTypes: DocumentType[];
}>();

const emit = defineEmits<{
  saved: [];
}>();

const toast = useToast();

const isOpen = ref(false);
const isLoading = ref(false);
const useLatest = ref(true);
const selectedPromptId = ref('');
const selectedVersion = ref('');
const selectedCoverageTypeId = ref('');
const selectedDocumentTypeId = ref('');

const publishedPromptIds = computed(() => {
  const ids = new Set<string>();
  for (const prompt of props.prompts) {
    if (!prompt.isDraft) ids.add(String(prompt.promptId));
  }
  return ids;
});

interface SelectOption {
  label: string;
  value: string;
}

const promptNameOptions = computed<SelectOption[]>(() => {
  const seen = new Set<string>();
  return props.prompts
    .filter((prompt) => {
      const id = String(prompt.promptId);
      if (seen.has(id) || !publishedPromptIds.value.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((prompt) => ({ label: prompt.name, value: String(prompt.promptId) }));
});

const versionOptions = computed<SelectOption[]>(() => {
  if (!selectedPromptId.value) return [];
  return props.prompts
    .filter((prompt) => String(prompt.promptId) === selectedPromptId.value && !prompt.isDraft)
    .map((prompt) => ({ label: `v${prompt.version}`, value: prompt.version }));
});

const coverageTypeOptions = computed<SelectOption[]>(() =>
  props.coverageTypes.map((type) => ({ label: type.name, value: type.id.toString() })),
);

const documentTypeOptions = computed<SelectOption[]>(() =>
  props.documentTypes
    .map((type) => ({ label: type.name, value: type.id.toString() }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

watch(selectedPromptId, () => {
  selectedVersion.value = '';
});

const isFormValid = computed(
  () =>
    !!selectedPromptId.value &&
    !!selectedCoverageTypeId.value &&
    !!selectedDocumentTypeId.value &&
    (useLatest.value || !!selectedVersion.value),
);

const resetForm = () => {
  selectedPromptId.value = '';
  selectedVersion.value = '';
  selectedCoverageTypeId.value = '';
  selectedDocumentTypeId.value = '';
  useLatest.value = true;
  isLoading.value = false;
};

const show = () => {
  resetForm();
  isOpen.value = true;
};

const handleCancel = () => {
  if (isLoading.value) return;
  isOpen.value = false;
};

const handleAction = async () => {
  if (!isFormValid.value) return;
  isLoading.value = true;
  try {
    const request: AIPromptMappingRequest = {
      promptId: Number(selectedPromptId.value),
      promptVersion: useLatest.value ? PROMPT_VERSION_LATEST : selectedVersion.value,
      coverageTypeId: parseInt(selectedCoverageTypeId.value),
      documentTypeId: parseInt(selectedDocumentTypeId.value),
    };
    await aiService.createPromptMapping(request);
    toast.success('Prompt mapping created');
    emit('saved');
    isOpen.value = false;
  } catch (error) {
    console.error('Failed to create prompt mapping:', error);
    toast.error('Failed to create prompt mapping');
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ show });
</script>

<template>
  <AppModal
    :show="isOpen"
    title="New Prompt Mapping"
    action-button-label="Create"
    :handle-cancel="handleCancel"
    :handle-action="handleAction"
    :loading="isLoading"
    :action-disabled="!isFormValid || isLoading"
    show-overflow>
    <template #content>
      <div class="space-y-6">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">
            Prompt <span class="text-red-500">*</span>
          </label>
          <AppSelect
            v-model="selectedPromptId"
            :options="promptNameOptions"
            placeholder="Select a prompt..."
            class="w-full" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">
            Version <span class="text-red-500">*</span>
          </label>
          <Checkbox
            v-model="useLatest"
            label="Latest (auto-updates when a new version is published)" />
          <AppSelect
            v-model="selectedVersion"
            :options="versionOptions"
            placeholder="Select a version..."
            :disabled="useLatest || !selectedPromptId"
            class="w-full" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">
            Coverage Type <span class="text-red-500">*</span>
          </label>
          <AppSelect
            v-model="selectedCoverageTypeId"
            :options="coverageTypeOptions"
            placeholder="Select coverage type..."
            class="w-full" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">
            Document Type <span class="text-red-500">*</span>
          </label>
          <AppSelect
            v-model="selectedDocumentTypeId"
            :options="documentTypeOptions"
            placeholder="Select document type..."
            class="w-full" />
        </div>
      </div>
    </template>
  </AppModal>
</template>
